import type { ConfigurationValidationError } from '@festina/configuration-general-types/configuration'
import { LayerKey, type WorkspaceKey } from '@festina-life/api'
import type { Optional } from '@festina-life/core'
import { useMemo } from 'react'

import { useMetaModelContext } from '../../context/metaModelContext'
import type { ValidationErrorWithLocation } from '../errors/types'
import { isWorkspaceEnumLiteralOperation } from '../templateOperations/types'
import { isOperationValidationError } from '../types'
import { useWorkspaceOperations } from '../workspace/useWorkspaceOperations'
import type { ConfiguredEnumLiteral } from './types'

export const useDraftEnumLiterals = (
  workspaceKey: Optional<WorkspaceKey>,
  errors: ValidationErrorWithLocation<ConfigurationValidationError>[],
): ConfiguredEnumLiteral[] => {
  const { data: draftOperations } = useWorkspaceOperations(
    workspaceKey,
    isWorkspaceEnumLiteralOperation,
  )
  const { blockTopology } = useMetaModelContext()
  const rootBlock = blockTopology.name

  return useMemo(() => {
    // We intentionally preserve old fetch data in the useFetchWorkspaceOperations hook
    // in order to make the live update experience nicer for the user. Therefore, we need
    // to check whether we're in a workspace and return [] if not, since in that case
    // the data in draftOperations is irrelevant.
    if (!workspaceKey) {
      return []
    }

    return draftOperations.map((workspaceOperation) => ({
      internalOperation: workspaceOperation.operation,
      changesetHistory: undefined,
      isDraft: true,
      layer: new LayerKey({
        tenantId: workspaceOperation.tenantId,
        layerId: workspaceOperation.layer,
      }),
      type: 'configured',
      annulOperations: [], // Draft literals cannot be annulled, since they are not yet part of the configuration. Therefore, this array will always be empty.
      errors: (errors ?? []).filter(
        ({ error }) =>
          isOperationValidationError(error) &&
          error.operationKey === workspaceOperation.operation.operationKey,
      ),
      actions: [],
      block: workspaceOperation.block ?? rootBlock,
    }))
  }, [draftOperations, errors, workspaceKey, rootBlock])
}
